import {
	IconClock,
	IconMail,
	IconSearch,
	IconShieldLock,
	IconTrash,
	IconWorld,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { buttonVariants } from "@/components/ui/button";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import {
	getStaggeredAnimationDelayStyle,
	MARKETING_SECTION_ANIM_BASE,
} from "@/utils/animation";
import { seo } from "@/utils/seo";

const featureFaqs = [
	{
		question: "Do I need an account to use ineedatest.email?",
		answer:
			"No. You pick a domain, set how long the inbox should live, and copy the address. The inbox URL is the only credential—there is no sign-up step.",
	},
	{
		question: "Can ineedatest.email send email on my behalf?",
		answer:
			"No. It is inbound-only. That keeps the surface area small: receive trials, OTPs, and receipts, then let the inbox expire.",
	},
	{
		question: "What happens when the timer runs out?",
		answer:
			"The inbox stops accepting new mail and existing messages are removed. The UI reflects that honestly instead of showing stale mail after expiry.",
	},
	{
		question: "Are attachments stored?",
		answer:
			"No. Text and HTML bodies are kept so you can read codes and copy links. Attachments are not persisted.",
	},
];

const pillars = [
	{
		icon: IconShieldLock,
		title: "The inbox URL is the login",
		body: "No account wall and no password reset flow. Save the link, open it from any device, and you are in—until the expiry you chose.",
	},
	{
		icon: IconClock,
		title: "Expiry you set upfront",
		body: "Pick a lifetime from 30 minutes to several days before you create the address. When time is up, the inbox is gone—not hidden behind a paywall.",
	},
	{
		icon: IconMail,
		title: "Inbound only",
		body: "Receive mail for trials, OTPs, and vendor sign-ups. Nothing is sent on your behalf, which matches how most throwaway workflows actually work.",
	},
	{
		icon: IconSearch,
		title: "Search inside the inbox",
		body: "Filter messages by subject or sender when a thread gets noisy. Handy for long test sessions with multiple senders.",
	},
	{
		icon: IconWorld,
		title: "Multiple receiving domains",
		body: "Choose which host your address uses at creation time. Useful when a service blocks one domain or you want addresses that look less generic.",
	},
	{
		icon: IconTrash,
		title: "Text and HTML, not attachments",
		body: "Bodies are stored so verification codes and receipts are readable. Binaries are not kept—less storage risk and fewer surprises.",
	},
];

const useCaseLinks = [
	{ to: "/for/testing" as const, label: "Disposable email for testing" },
	{ to: "/for/sign-up" as const, label: "Temporary email for sign up" },
	{ to: "/for/no-registration" as const, label: "No registration required" },
] as const;

const alternativeLinks = [
	{ to: "/alternatives/mailinator" as const, label: "Mailinator" },
	{ to: "/alternatives/guerrilla-mail" as const, label: "Guerrilla Mail" },
	{ to: "/alternatives/temp-mail" as const, label: "Temp Mail" },
	{ to: "/alternatives/10minutemail" as const, label: "10MinuteMail" },
] as const;

export const Route = createFileRoute("/_marketing/features")({
	component: RouteComponent,
	head: () =>
		seo({
			title: `Disposable email features — ${config.siteName}`,
			description: `${config.siteName} features: URL-based disposable inboxes, honest expiry, inbound-only mail, inbox search, and multiple domains—no account required.`,
			keywords:
				"disposable email features, temporary email, throwaway inbox, inbound only, no registration, email expiry",
			type: "FAQPage",
			faqItems: featureFaqs,
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			<section className="pt-20 pb-12 sm:pt-28 sm:pb-16">
				<MarketingContainer>
					<MarketingHeader
						align="left"
						title="Features"
						heading="Everything you get in a disposable inbox"
						description="Built for the moment you need a real address that receives mail—and nothing more. No SaaS dashboard, no outbound sending, no account to forget."
						actions={
							<Link
								to="/"
								className={cn(
									buttonVariants({ variant: "default", size: "lg" }),
									"rounded-full px-5",
								)}
							>
								Create an address
							</Link>
						}
					/>
				</MarketingContainer>
			</section>

			<section className="border-t border-border/60 pb-16 sm:pb-20">
				<MarketingContainer>
					<div className={cn(MARKETING_SECTION_ANIM_BASE, "delay-200")}>
						<div className="grid gap-6 pt-12 sm:gap-8 sm:pt-16 lg:grid-cols-2">
							{pillars.map((item, index) => {
								const Icon = item.icon;
								return (
									<div
										key={item.title}
										className={cn(
											"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
											MARKETING_SECTION_ANIM_BASE,
										)}
										style={getStaggeredAnimationDelayStyle(index, 0.4)}
									>
										<div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
											<Icon className="size-6 text-primary" aria-hidden />
										</div>
										<div className="flex min-w-0 flex-col gap-2">
											<h2 className="text-xl font-semibold tracking-tight text-foreground">
												{item.title}
											</h2>
											<p className="text-base text-pretty text-muted-foreground">
												{item.body}
											</p>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</MarketingContainer>
			</section>

			<section className="border-t border-border/60 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						align="left"
						headingAs="h2"
						title="Use cases"
						heading="Common jobs this inbox handles"
						description="Short guides for how people actually use throwaway addresses."
						className="mb-10 sm:mb-12"
					/>
					<ul className="grid gap-4 sm:grid-cols-3">
						{useCaseLinks.map((item) => (
							<li key={item.to}>
								<Link
									to={item.to}
									className="block rounded-xl border border-border/80 bg-card/40 p-5 text-base font-medium text-foreground ring-1 ring-border/40 transition-colors hover:border-primary/40 hover:text-primary"
								>
									{item.label}
								</Link>
							</li>
						))}
					</ul>
					<p className="mt-8 text-sm text-muted-foreground">
						How mail is handled under the hood:{" "}
						<Link
							to="/transparency"
							className="font-medium text-primary underline-offset-4 hover:underline"
						>
							transparency
						</Link>
						.
					</p>
				</MarketingContainer>
			</section>

			<section className="border-t border-border/60 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						align="left"
						headingAs="h2"
						title="FAQ"
						heading="Feature questions"
						className="mb-10 sm:mb-12"
					/>
					<dl className="space-y-8">
						{featureFaqs.map((faq) => (
							<div key={faq.question}>
								<dt className="text-lg font-semibold text-foreground">
									{faq.question}
								</dt>
								<dd className="mt-2 text-base text-pretty text-muted-foreground">
									{faq.answer}
								</dd>
							</div>
						))}
					</dl>
				</MarketingContainer>
			</section>

			<section className="border-t border-border/60 pb-20 sm:pb-28">
				<MarketingContainer>
					<div
						className={cn(
							"mt-4 rounded-2xl border border-dashed border-border/80 bg-muted/15 px-6 py-10 text-center sm:px-10",
							cn(MARKETING_SECTION_ANIM_BASE, "delay-500"),
						)}
					>
						<p className="mx-auto max-w-[52ch] text-lg text-pretty text-muted-foreground">
							Ready to try it? Create an address on the homepage, copy the inbox
							link, and use it wherever you would paste a throwaway email.
						</p>
						<Link
							to="/"
							className={cn(
								buttonVariants({ variant: "default", size: "lg" }),
								"mt-6",
							)}
						>
							Open the inbox generator
						</Link>
					</div>

					<div className="mt-10 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
						<span>Compare alternatives:</span>
						{alternativeLinks.map((item) => (
							<Link
								key={item.to}
								to={item.to}
								className="text-primary underline-offset-4 hover:underline"
							>
								{item.label}
							</Link>
						))}
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
